import type { WhoAmIClient } from '@tebuto/api/clients/models/WhoAmIClient'
import { ERROR_MESSAGES } from '@tebuto/functions/api'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import type { BookEventFormValues } from './BookingEvent'
import type { APIError, EventCategory, ParsedBookableEvent } from './types'
import { EventLocation, MeetingProvider } from './types'

export function resolveCheckoutCustomerFields(
    bookingData: BookEventFormValues | null | undefined,
    client: WhoAmIClient | null | undefined
): {
    firstName: string
    lastName: string
    email: string
    address?: BookEventFormValues['address']
} {
    const firstName = bookingData?.firstName?.trim() || client?.firstName?.trim() || ''
    const lastName = bookingData?.lastName?.trim() || client?.lastName?.trim() || ''
    const email = bookingData?.email?.trim() || client?.email?.trim() || ''

    const address =
        bookingData?.address ??
        (client?.address
            ? {
                  streetAndNumber: client.address.streetAndNumber,
                  city: {
                      zip: client.address.city.zip,
                      name: client.address.city.name
                  }
              }
            : undefined)

    return address ? { firstName, lastName, email, address } : { firstName, lastName, email }
}

export function resolveEventLocation(
    event: ParsedBookableEvent,
    categories: Record<number, EventCategory>
): EventLocation {
    return event.location ?? categories[event.categoryId]?.location ?? EventLocation.NOT_FIXED
}

export function resolveEventOnlineMeetingProvider(
    event: ParsedBookableEvent,
    category: EventCategory | undefined
): MeetingProvider | undefined {
    return event.onlineMeetingProvider ?? category?.onlineMeetingProvider
}

export function useGetMeetingProviderLabel() {
    const { t } = useComponentTranslation()
    return (provider?: MeetingProvider) => {
        switch (provider) {
            case MeetingProvider.ZOOM:
                return t('zoom')
            case MeetingProvider.MICROSOFT_TEAMS:
                return t('microsoftTeams')
            case MeetingProvider.TEBUTO_MEET:
                return t('tebutoMeet')
            default:
                return t('unknown')
        }
    }
}

export function isEventAlreadyBookedError(error: APIError): boolean {
    return isAlreadyBookedErrorType(error.type)
}

export function isAlreadyBookedErrorType(errorType: string): boolean {
    const errorsToShowModal: (keyof typeof ERROR_MESSAGES)[] = [
        'CannotBookAnEventThatConflictsWithAnotherEvent',
        'EventIsNotAvailableForBooking'
    ]
    return errorsToShowModal.includes(errorType as keyof typeof ERROR_MESSAGES)
}
